import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axiosInstance from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { setAccessToken, setUserData, setPreSelectedCompany, completeGoogleRedirectAction, LOGOUT_ACTION } from '../store/actions/AuthActions';
import { refreshBrowserSession, clearBrowserIdentity, onBrowserSessionChange, browserSignedOut, sessionMarker, sessionChangedError } from '../services/BrowserSessionService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();
    const history = useHistory();

    const initializeAuth = async () => {
        const marker = sessionMarker();
        try {
            setIsLoading(true);

            // Attempt to refresh access token
            const accessToken = await refreshBrowserSession();

            // Store access token in state
            dispatch(setAccessToken(accessToken));

            // Fetch user data
            const userApiUrlPath = getApiUrl('auth/users');
            const userResponse = await axiosInstance.get(userApiUrlPath);
            const userData = userResponse.data;

            const userId = userData.user.google_id;

            // Fetch user's associated companies
            const companiesApiUrlPath = getApiUrl(`auth/users/companies?googleId=${userId}`);
            const companiesResponse = await axiosInstance.get(companiesApiUrlPath, { withCredentials: true });
            const companiesData = companiesResponse.data;
            if (marker !== sessionMarker()) throw sessionChangedError();

            // Convert companies array to object for easy access
            const companiesObject = {};
            // Handle both wrapped response { companies: [...] } and direct array [...]
            const companiesArray = companiesData.companies || companiesData;
            if (companiesArray && Array.isArray(companiesArray)) {
                companiesArray.forEach(company => {
                    companiesObject[company.company_id] = {
                        companyName: company.company_name,
                        companyRegNo: company.company_reg_no || '',
                        companyType: company.company_type || '',
                        vatStatus: company.vat_status || '',
                        registrationNumber: company.registration_number || '',
                        // DEPRECATED (Phase 1.2): the `roles` field on the
                        // Redux store is unread by any component (verified
                        // in the v2 redesign review at
                        // docs/ACCESS_CONTROL_REDESIGN.md §1.7). For caller-
                        // self role/capability checks in the active company,
                        // use useCapabilities() from
                        // src/context/CapabilityContext.js instead. The
                        // write is retained until a Phase 1.5+ cleanup
                        // confirms no late-bound consumers (e.g., dynamic
                        // imports, third-party widgets) rely on it.
                        roles: company.roles || []
                    };
                });
            }

            // Dispatch user data with companies
            dispatch(setUserData({
                ...userData,
                companies: companiesObject
            }));

            // Check for previously selected company in localStorage
            const storedCompanyString = localStorage.getItem('selectedCompany');

            if (storedCompanyString) {
                try {
                    const storedCompany = JSON.parse(storedCompanyString);

                    // Verify the stored company still exists in user's companies
                    const companyExists = Object.keys(companiesObject).includes(storedCompany.companyId);

                    if (companyExists) {
                        // ORDER MATTERS: setUserData above is dispatched synchronously,
                        // so state.auth.user.companies is populated before this thunk
                        // reads it for the Session-B extras (companyType / vatStatus /
                        // registrationNumber). Reordering will silently break the badge
                        // and the Phase-3 allowedCompanyTypes guard.
                        dispatch(setPreSelectedCompany(storedCompany.companyId, storedCompany.companyName));
                    } else {
                        localStorage.removeItem('selectedCompany');
                    }
                } catch (parseError) {
                    localStorage.removeItem('selectedCompany');
                }
            }

        } catch (error) {
            if (error.response?.status === 401 && marker === sessionMarker()) {
                dispatch({ type: LOGOUT_ACTION });
                // An anonymous boot may be resuming an unverified invitation.
                // Preserve Firebase proof until a website session has existed.
                await clearBrowserIdentity({ notify: !!marker, firebase: !!marker }).catch(() => {});
            }
        } finally {
            setIsLoading(false);
            setIsInitialized(true);
        }
    };

    useEffect(() => {
        const boot = async () => {
            const handled = await dispatch(completeGoogleRedirectAction(history));
            if (!handled) await initializeAuth();
            else { setIsLoading(false); setIsInitialized(true); }
        };
        boot();
        return onBrowserSessionChange(() => {
            dispatch({ type: LOGOUT_ACTION });
            if (browserSignedOut()) clearBrowserIdentity({ notify: false }).catch(() => {});
            else initializeAuth();
        });
    }, []);

    const value = {
        isInitialized,
        isLoading,
        refresh: initializeAuth
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
